import * as THREE from 'three';
import { TweenMax } from 'gsap';

export module CanvasModule {
  export class Service {
    private renderer?: THREE.WebGLRenderer;
    private canvas: HTMLCanvasElement | null = null;
    private scene?: THREE.Scene;
    private camera?: THREE.OrthographicCamera;
    private mat?: THREE.RawShaderMaterial;
    private textureArr: THREE.Texture[] = [];
    private obj = { trans: 0 };
    private imageSizeMap: Map<string, { width: number, height: number }> = new Map();

    constructor() {
      this.canvas = document.getElementById('canvasAnimation') as HTMLCanvasElement;
      if (this.canvas) {
        this.initializeCanvasAnimation = this.initializeCanvasAnimation.bind(this);
        // イベントリスナーの追加
        this.addEventListeners();

      }
    }

    private addEventListeners() {
      document.addEventListener('readCompleteAction', () => {
        this.initializeCanvasAnimation()
      });
    }

    private initializeCanvasAnimation() {
      if (!this.canvas) return;

      this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas });
      this.scene = new THREE.Scene();

      this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 1);
      this.camera.position.z = 1;

      const assetUrls = [
        `/JMS/2023/IMAGES/ANIMATIONS/TOP/kv@2x.jpg`,
        `/JMS/2023/IMAGES/TOP/SP/kv@2x.jpg`,
        `/JMS/2023/IMAGES/TOP/texture.jpg`,
      ];

      const accessTime = new Date().getTime();

      assetUrls.forEach((url) => {
        const img = new Image();
        const texture = new THREE.Texture();
        texture.flipY = false;
        this.textureArr.push(texture);

        img.onload = () => {
          // 画像のサイズ情報をマップに格納
          this.imageSizeMap.set(url, { width: img.width, height: img.height });

          texture.image = img;
          texture.needsUpdate = true;
          if (this.textureArr.length === assetUrls.length) {
            this.startAnimationLoop();
            const now = new Date().getTime();
            const elapsed = now - accessTime;
            const waitTime = Math.max(2000 - elapsed, 0);

            setTimeout(() => {
              // イベント発火処理
              this.startAnimationLoop();
              const body = document.querySelector('body');
              if (body) {
                body.classList.add('is-loaded');
              }
            }, waitTime);
          }
        };
        img.crossOrigin = "Anonymous";
        img.src = url;
      });

      const vertexSrc = `
        precision mediump float;
        attribute vec4 position;
        attribute vec2 uv;
        varying vec2 vUv;
        void main() {
          gl_Position = position;
          vUv = uv;
        }
      `;

      const fragmentSrc = `
        precision mediump float;
        uniform float uTrans;
        uniform sampler2D uTexture1;
        uniform sampler2D uDisp;
        uniform vec2 u_resolution;
        uniform vec2 u_imageResolution;
        uniform float u_scrollY;
        uniform float u_time;
        varying vec2 vUv;
        float easeOutExpo(float t) {
          return t == 1.0 ? 1.0 : 1.0 - pow(2.0, -10.0 * t);
        }
        void main() {
          vec2 ratio = vec2(
            min((u_resolution.x / u_resolution.y) / (u_imageResolution.x / u_imageResolution.y), 1.0),
            min((u_resolution.y / u_resolution.x) / (u_imageResolution.y / u_imageResolution.x), 1.0)
          );
          vec2 uv = vec2(
            vUv.x * ratio.x + (1.0 - ratio.x) * 0.5,
            vUv.y * ratio.y + (1.0 - ratio.y) * 0.5
          );
          vec2 center = vec2(0.5);
          vec2 offsetFromCenter = uv - center;
          vec4 disp = texture2D(uDisp, center + offsetFromCenter * (0.2 + 0.8 * (1.0 - uTrans)));
          float distanceFromCenter = length(offsetFromCenter);
          float trans = clamp(1.6 * uTrans - disp.r - distanceFromCenter * 0.5, 0.0, 1.0);
          trans = easeOutExpo(trans);
          vec2 color1Uv = center + (uv - center) * (trans);
          vec4 color1 = texture2D(uTexture1, vec2(color1Uv.x, 1.0 - color1Uv.y)); // 画像を反転しないように修正
          gl_FragColor = mix(vec4(0.0), color1, trans);
        }
      `;

      this.mat = new THREE.RawShaderMaterial({
        uniforms: {
          uTrans: { value: this.obj.trans },
          uTexture1: { value: this.textureArr[1] },
          uDisp: { value: this.textureArr[2] },
          u_resolution: { value: new THREE.Vector2(this.renderer.domElement.width, this.renderer.domElement.height) },
          u_imageResolution: { value: new THREE.Vector2(0, 0) },
          u_scrollY: { value: 0.0 }
        },
        vertexShader: vertexSrc,
        fragmentShader: fragmentSrc,
      });

      const geo = new THREE.PlaneGeometry(2, 2);
      const mesh = new THREE.Mesh(geo, this.mat);
      this.scene.add(mesh);

      this.resize();


      window.addEventListener("resize", this.resize.bind(this));
      this.canvas.addEventListener('click', this.handleClick.bind(this));
      window.addEventListener('scroll', this.handleScroll.bind(this));

      window.onload = () => {
        this.animateClickEvent();
      };
    }

    private startAnimationLoop() {
      const loop = () => {
        if (this.mat && this.renderer && this.scene && this.camera) {
          this.mat.uniforms.uTrans.value = this.obj.trans;
          this.renderer.render(this.scene, this.camera);
        }
        requestAnimationFrame(loop);
      }
      loop();
    }

    private handleClick() {
      TweenMax.killTweensOf(this.obj);
      if (this.obj.trans === 0) {
        TweenMax.to(this.obj, 1, { trans: 1 });
      } else {
        TweenMax.to(this.obj, 1, { trans: 0 });
      }
    }

    private handleScroll(e: Event) {
      if (this.mat) {
        this.mat.uniforms.u_scrollY.value = window.scrollY;
      }
    }

    private animateClickEvent() {
      this.handleClick();
    }

    private resize() {
      if (this.renderer && this.mat && this.scene && this.camera) {
        const scale = window.visualViewport?.scale ?? 1
        const currentWidth = window.innerWidth * scale
        const currentHeight = window.innerHeight * scale

        this.renderer.setSize(currentWidth, currentHeight);
        this.mat.uniforms.u_resolution.value.x = currentWidth;
        this.mat.uniforms.u_resolution.value.y = currentHeight;

        let imageUrl: string;
        let defaultResolution: THREE.Vector2;

        if (currentWidth >= 1024) {
          imageUrl = '/JMS/2023/ANIMATIONS/IMAGES/TOP/kv@2x.jpg';
          defaultResolution = new THREE.Vector2(2560, 1439); // デフォルト値
          this.mat.uniforms.uTexture1.value = this.textureArr[0];
        } else {
          imageUrl = '/JMS/2023/IMAGES/TOP/sp/kv@2x.jpg';
          defaultResolution = new THREE.Vector2(1920, 1920); // デフォルト値
          this.mat.uniforms.uTexture1.value = this.textureArr[1];
        }

        // imageSizeMap から画像のサイズを取得
        const imageSize = this.imageSizeMap.get(imageUrl);

        // サイズ情報がある場合はそれを使用、ない場合はデフォルト値を使用
        this.mat.uniforms.u_imageResolution.value = imageSize
          ? new THREE.Vector2(imageSize.width, imageSize.height)
          : defaultResolution;

        this.mat.uniforms.uTrans.value = this.obj.trans
        this.renderer.render(this.scene, this.camera)
      }
    }

  }
}
